/**
 * OrderCommandHeader — Mission Control strip (Sprint 8.6)
 * Answers: where, health, next, owner, blockers (outstanding).
 * Retires the competing mini status stepper.
 */

import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Divider,
  Stack,
  Menu,
  MenuItem,
  ListItemText,
  CircularProgress,
  Drawer,
  List,
  ListSubheader,
  ListItemButton,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack, Edit, MoreVert, Update } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { format } from "date-fns";
import { ConfigProvider } from "antd";
import { bubakiiTheme } from "../../../theme/bubakiiTheme.js";
import DeliveryHealthChip from "./DeliveryHealthChip.jsx";
import ProviderAvatar from "./ProviderAvatar.jsx";
import { computeOrderHealth } from "../services/orderOperationalStatus.js";
import { getMilestoneView } from "../services/orderMilestoneView.js";
import {
  getAssignedOwner,
  getCorridorLabel,
  getStageProgress,
} from "../services/orderMissionSummary.js";

const upper = (v) => String(v || "").replaceAll("_", " ").toUpperCase();

const formatMoney = (value) => {
  const num = Number(value || 0);
  return `R${num.toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const safeFormat = (value) => {
  if (!value) return "N/A";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return format(d, "PPp");
  } catch {
    return String(value);
  }
};

function MissionStat({ label, children }) {
  return (
    <Box sx={{ minWidth: 120, flex: "1 1 120px" }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: 0.04, fontWeight: 700 }}
      >
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

export default function OrderCommandHeader({
  order,
  trip = null,
  invoicesRollup,
  healthThresholds,
  hasBooking,
  onBack,
  onUpdateStatus,
  quickStatusOptions = [],
  allStatusOptions = [],
  onQuickStatusSelect,
  statusUpdating = false,
  onEdit,
  onViewBooking,
  onViewIncidents,
  onViewCompensation,
  onDownloadDriverPdf,
  onDownloadClientPdf,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusSheetOpen, setStatusSheetOpen] = useState(false);
  const moreOpen = Boolean(moreAnchorEl);
  const statusMenuOpen = Boolean(statusAnchorEl);

  const health = useMemo(
    () => computeOrderHealth(order, healthThresholds),
    [order, healthThresholds]
  );
  const milestone = useMemo(
    () => getMilestoneView(order, trip),
    [order, trip]
  );
  const corridor = useMemo(() => getCorridorLabel(order), [order]);
  const owner = useMemo(() => getAssignedOwner(order), [order]);
  const progress = useMemo(
    () => getStageProgress(order, trip),
    [order, trip]
  );

  const balance = Number(invoicesRollup?.balance || 0);
  const hasOutstanding = Number.isFinite(balance) && balance > 0.01;
  const quickStatusValues = useMemo(
    () => new Set(quickStatusOptions.map((option) => option.value)),
    [quickStatusOptions]
  );
  const remainingStatusOptions = useMemo(
    () =>
      allStatusOptions.filter((option) => !quickStatusValues.has(option.value)),
    [allStatusOptions, quickStatusValues]
  );

  const statusLabel = upper(order?.status || "Unknown");

  return (
    <Card sx={{ mb: 3 }} data-testid="order-mission-header">
      <CardContent>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 0, flex: "1 1 280px" }}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="overline" color="text.secondary">
                ORDER
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {order?.id || "—"}
              </Typography>
              <ConfigProvider theme={bubakiiTheme}>
                <DeliveryHealthChip
                  health={health.health}
                  reason={health.reason}
                  hoursSinceUpdate={health.hoursSinceUpdate}
                  staleUpdateHours={healthThresholds?.staleUpdateHours}
                  applicable={health.applicable}
                />
              </ConfigProvider>
              {order?.priority ? (
                <Chip
                  size="small"
                  label={`PRIORITY: ${upper(order.priority)}`}
                  color="warning"
                  variant="outlined"
                />
              ) : null}
              <Chip
                size="small"
                label={statusLabel}
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{ textTransform: "none" }}
            >
              Back
            </Button>

            <Button
              variant="contained"
              startIcon={<Update />}
              onClick={(e) => {
                if (isMobile) {
                  setStatusSheetOpen(true);
                  return;
                }
                setStatusAnchorEl(e.currentTarget);
              }}
              disabled={statusUpdating}
              sx={{ textTransform: "none" }}
            >
              Update Status
            </Button>

            <Menu
              anchorEl={statusAnchorEl}
              open={statusMenuOpen}
              onClose={() => setStatusAnchorEl(null)}
            >
              {quickStatusOptions.length > 0 ? (
                <MenuItem disabled>
                  <ListItemText primary="Quick actions" />
                </MenuItem>
              ) : null}
              {quickStatusOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  disabled={Boolean(option.disabled) || statusUpdating}
                  onClick={() => {
                    setStatusAnchorEl(null);
                    if (option.disabled || statusUpdating) return;
                    onQuickStatusSelect?.(option.value);
                  }}
                >
                  <ListItemText
                    primary={option.label}
                    secondary={option.helperText || null}
                  />
                </MenuItem>
              ))}
              {remainingStatusOptions.length > 0 ? <Divider /> : null}
              {remainingStatusOptions.length > 0 ? (
                <MenuItem disabled>
                  <ListItemText primary="All statuses" />
                </MenuItem>
              ) : null}
              {remainingStatusOptions.map((option) => (
                <MenuItem
                  key={`all-${option.value}`}
                  disabled={Boolean(option.disabled) || statusUpdating}
                  onClick={() => {
                    setStatusAnchorEl(null);
                    if (option.disabled || statusUpdating) return;
                    onQuickStatusSelect?.(option.value);
                  }}
                >
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
              <Divider />
              <MenuItem
                onClick={() => {
                  setStatusAnchorEl(null);
                  onUpdateStatus?.();
                }}
              >
                Open full order form
              </MenuItem>
              {statusUpdating ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Updating status...
                </MenuItem>
              ) : null}
            </Menu>

            <Drawer
              anchor="bottom"
              open={statusSheetOpen}
              onClose={() => setStatusSheetOpen(false)}
              PaperProps={{
                sx: {
                  borderTopLeftRadius: 14,
                  borderTopRightRadius: 14,
                  pb: 1,
                },
              }}
            >
              <List
                subheader={
                  <ListSubheader sx={{ lineHeight: 1.4, py: 1.5 }}>
                    Update status
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Current: {statusLabel}
                    </Typography>
                  </ListSubheader>
                }
              >
                {quickStatusOptions.length > 0 ? (
                  <ListSubheader
                    disableSticky
                    sx={{ bgcolor: "background.paper" }}
                  >
                    Quick actions
                  </ListSubheader>
                ) : null}
                {quickStatusOptions.map((option) => (
                  <ListItemButton
                    key={`quick-mobile-${option.value}`}
                    disabled={Boolean(option.disabled) || statusUpdating}
                    onClick={() => {
                      setStatusSheetOpen(false);
                      if (option.disabled || statusUpdating) return;
                      onQuickStatusSelect?.(option.value);
                    }}
                  >
                    <ListItemText
                      primary={option.label}
                      secondary={option.helperText || null}
                    />
                  </ListItemButton>
                ))}
                {remainingStatusOptions.length > 0 ? (
                  <ListSubheader
                    disableSticky
                    sx={{ bgcolor: "background.paper" }}
                  >
                    All statuses
                  </ListSubheader>
                ) : null}
                {remainingStatusOptions.map((option) => (
                  <ListItemButton
                    key={`all-mobile-${option.value}`}
                    disabled={Boolean(option.disabled) || statusUpdating}
                    onClick={() => {
                      setStatusSheetOpen(false);
                      if (option.disabled || statusUpdating) return;
                      onQuickStatusSelect?.(option.value);
                    }}
                  >
                    <ListItemText primary={option.label} />
                  </ListItemButton>
                ))}
                <Divider sx={{ my: 1 }} />
                <ListItemButton
                  onClick={() => {
                    setStatusSheetOpen(false);
                    onUpdateStatus?.();
                  }}
                >
                  <ListItemText primary="Open full order form" />
                </ListItemButton>
                {statusUpdating ? (
                  <ListItemButton disabled>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <ListItemText primary="Updating status..." />
                  </ListItemButton>
                ) : null}
              </List>
            </Drawer>

            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={onEdit}
              sx={{ textTransform: "none" }}
            >
              Edit
            </Button>

            <Button
              variant="outlined"
              startIcon={<MoreVert />}
              onClick={(e) => setMoreAnchorEl(e.currentTarget)}
              sx={{ textTransform: "none" }}
            >
              More
            </Button>

            <Menu
              anchorEl={moreAnchorEl}
              open={moreOpen}
              onClose={() => setMoreAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setMoreAnchorEl(null);
                  onViewBooking?.();
                }}
                disabled={!hasBooking}
              >
                Booking details
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMoreAnchorEl(null);
                  onViewIncidents?.();
                }}
              >
                Incidents
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMoreAnchorEl(null);
                  onViewCompensation?.();
                }}
              >
                Compensation & recovery
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setMoreAnchorEl(null);
                  onDownloadDriverPdf?.();
                }}
              >
                Documents → Driver pickup PDF
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMoreAnchorEl(null);
                  onDownloadClientPdf?.();
                }}
              >
                Documents → Client pickup PDF
              </MenuItem>
            </Menu>
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <MissionStat label="Journey">
            <Typography variant="body1" fontWeight={700}>
              {corridor.origin}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              → {corridor.destination}
            </Typography>
          </MissionStat>

          <MissionStat label="Current">
            <Typography variant="body1" fontWeight={700}>
              {milestone.current?.title || statusLabel}
            </Typography>
          </MissionStat>

          <MissionStat label="Next">
            <Typography variant="body1" fontWeight={700}>
              {milestone.next?.title || "—"}
            </Typography>
            {order?.estimatedDelivery ? (
              <Typography variant="caption" color="text.secondary">
                ETA {safeFormat(order.estimatedDelivery)}
              </Typography>
            ) : null}
          </MissionStat>

          <MissionStat label="Assigned">
            <Box display="flex" alignItems="center" gap={1}>
              <ProviderAvatar name={owner.partnerName} size={28} showName={false} />
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  {owner.partnerName || "Unassigned"}
                </Typography>
                {owner.driverName ? (
                  <Typography variant="caption" color="text.secondary">
                    Driver: {owner.driverName}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          </MissionStat>

          <MissionStat label="Outstanding">
            {hasOutstanding ? (
              <Chip
                size="small"
                color="warning"
                label={formatMoney(balance)}
                data-testid="mission-outstanding"
              />
            ) : (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label="Clear"
              />
            )}
          </MissionStat>

          <MissionStat label={progress.label}>
            {progress.percent != null ? (
              <Typography
                variant="body1"
                fontWeight={700}
                data-testid="mission-progress"
              >
                {progress.percent}%
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            )}
          </MissionStat>
        </Stack>
      </CardContent>
    </Card>
  );
}
